import { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { ClipboardEvent, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'

interface OtpInputProps {
  /** Digits entered so far (no gaps), at most `length` characters. */
  value: string
  onChange: (value: string) => void
  length?: number
  autoFocus?: boolean
  disabled?: boolean
  invalid?: boolean
  /** id of the first box, so a <label htmlFor> can target it. */
  id?: string
  describedBy?: string
}

const onlyDigits = (s: string) => s.replace(/\D/g, '')

/**
 * Segmented one-time-code input (design turn 12c). Typing advances,
 * Backspace steps back, pasting a full code fills every box. The first box
 * carries autocomplete="one-time-code" so iOS/Android can offer SMS/email
 * codes.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  autoFocus,
  disabled,
  invalid,
  id,
  describedBy,
}: OtpInputProps) {
  const { t } = useTranslation('auth')
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = onlyDigits(value).slice(0, length)
  // Focus moves that depend on the new value wait for the parent to
  // re-render; focusing synchronously would run onFocus with the old value.
  const pendingFocus = useRef<number | null>(null)

  const focusBox = (index: number) => {
    const clamped = Math.max(0, Math.min(length - 1, index))
    const el = refs.current[clamped]
    el?.focus()
    el?.select()
  }

  useLayoutEffect(() => {
    if (pendingFocus.current === null) return
    const target = pendingFocus.current
    pendingFocus.current = null
    focusBox(target)
  })

  const update = (next: string, focusIndex: number) => {
    if (next === digits) {
      focusBox(focusIndex)
      return
    }
    pendingFocus.current = focusIndex
    onChange(next)
  }

  const writeFrom = (index: number, incoming: string) => {
    const chars = onlyDigits(incoming)
    if (!chars) return
    // A full-length paste/autofill always replaces the whole code.
    const start = chars.length >= length ? 0 : Math.min(index, digits.length)
    const next = (digits.slice(0, start) + chars + digits.slice(start + chars.length)).slice(0, length)
    update(next, Math.min(start + chars.length, length - 1))
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (index < digits.length) {
        const next = digits.slice(0, index) + digits.slice(index + 1)
        update(next, index > 0 && index === digits.length - 1 ? index - 1 : index)
      } else if (digits.length > 0) {
        update(digits.slice(0, -1), digits.length - 1)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusBox(index - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusBox(Math.min(index + 1, digits.length))
    }
  }

  const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    writeFrom(index, e.clipboardData.getData('text'))
  }

  return (
    <div className="flex gap-2" role="group" aria-label={t('gateway.code.label')}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          id={index === 0 ? id : undefined}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={length}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && index === 0}
          disabled={disabled}
          aria-label={t('gateway.code.digit', { index: index + 1, total: length })}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          value={digits[index] ?? ''}
          onFocus={(e) => {
            // Keep the code gap-free: jump to the first empty box.
            if (index > digits.length) focusBox(digits.length)
            else e.currentTarget.select()
          }}
          onChange={(e) => {
            const raw = onlyDigits(e.target.value)
            const current = digits[index]
            // Typing into a filled box whose digit wasn't selected yields two
            // chars (old + new): keep only the new one. Autofill/paste of a
            // longer code goes through untouched.
            const incoming =
              raw.length === 2 && current
                ? raw.startsWith(current)
                  ? raw.slice(1)
                  : raw.slice(0, 1)
                : raw
            writeFrom(index, incoming)
          }}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          className={cn(
            'h-[52px] w-12 min-w-0 flex-1 rounded-lg border border-input bg-card text-center font-mono text-xl font-medium tabular-nums outline-none transition-[border-color,box-shadow] sm:flex-none',
            'focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-foreground/15',
            'disabled:cursor-not-allowed disabled:opacity-50',
            invalid && 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20',
          )}
        />
      ))}
    </div>
  )
}
